import QueryString from "query-string";
import React from "react";
import * as Constants from "../data/StringConstants";
import { ROUTE_URIS } from "../data/StringConstants";
import AllFoldersContainer from "../views/FoldersPage/AllFolders/AllFoldersContainer";
import ArchivedFoldersContainer from "../views/FoldersPage/ArchivedFolders/ArchivedFoldersContainer";
import MyFoldersContainer from "../views/FoldersPage/MyFolders/MyFoldersContainer";
import RecentlyUpdatedFoldersContainer from "../views/FoldersPage/RecentlyUpdated/RecentlyUpdatedFoldersContainer";
import StarredFoldersContainer from "../views/FoldersPage/StarredFolder/StarredFoldersContainer";


/**
 * @description Determines whether or not the current route the user has navigated to is the
 * details view of a Folder/Promotion.
 * @param {string} currentHash The current route
 * @returns {boolean}
 */
export function isFolderDetailsRoute(currentHash) {
    const foldersRegex = /^#!\/folders\/(\d+)[\/]?(\?.*)?$/;

    return foldersRegex.test(currentHash);
}

export function isAllItemsRoute(currentHash) {
    const allItemsRegex = /^#!\/folders\/items?(\?.*)?$/;

    return allItemsRegex.test(currentHash);
}

/**
 * @description Parses the folder id from the folder details route
 * @tutorial currentHash["#!/folders/4"] given as param
 * Then currentHash is evaluated for truthiness
 * When currentHash is truthy, then try to parse out the folder's identifier
 * Then evaluate the parsedInt for truthiness
 * @param {string} currentHash The current route
 * @returns {int|boolean} FolderId[int] of the current Folder, otherwise false
 */
export function parseFolderIdFromCurrentHash(currentHash) {
    const parsedId = parseInt(currentHash && currentHash.split("folders/").pop().split("/")[0].split("?")[0]);
    return parsedId ? parsedId : false;
}

/**
 * @description Parses the query string from the given route
 * @tutorial currentHash["#!/folders/4"] given as param
 * Then currentHash is evaluated for truthiness
 * @param {string} currentHash The current route
 * @returns {string:boolean} the queryString, beginning with ?, or false if currentHash is falsey or no queryString found
 */
export function getQueryStringFromCurrentHash(currentHash) {
    const queryStringStartIndex = currentHash && currentHash.indexOf("?");
    return currentHash && queryStringStartIndex != -1 ? currentHash.slice(queryStringStartIndex, currentHash.length) : false;
}

/**
 * @description Removes the query string from the given route
 * @tutorial currentHash["#!/folders/4"] given as param
 * Then currentHash is evaluated for truthiness
 * @param {string} currentHash The current route
 * @returns {string:boolean} the current hash sans query string, or false if currentHash is falsey
 */
export function removeQueryStringFromCurrentHash(currentHash) {
    let queryStringStartIndex = currentHash && currentHash.indexOf("?");
    return currentHash && queryStringStartIndex != -1 ? currentHash.slice(0, queryStringStartIndex) : currentHash;
}

/**
 * @description Parses the query params object from the given route
 * @tutorial currentHash["#!/folders/4"] given as param
 * Then currentHash is parsed by third party package query-string
 * @param {string} currentHash The current route
 * @returns {object} queryParams object containing any data parsed from the queryString, or empty if there is none
 */
export function getQueryParamsFromCurrentHash(currentHash) {
    const queryString = getQueryStringFromCurrentHash(currentHash);
    return QueryString.parse(queryString);
}

/**
 * @description Removed exising queryParams from a hash, if present, and adds stringified queryParams to the hash
 * @tutorial hash["#!/folders/4"] given as param
 * Then hash is parsed by third party package query-string
 * @param {string} currentHash The current route
 * @param {string} queryParams The query params to add to the hash
 * @returns {string} string of current hash with stringified queryParams
 */
export function addQueryParamsToHash(hash, queryParams) {
    const baseRoute = removeQueryStringFromCurrentHash(hash);
    const queryString = QueryString.stringify(queryParams);
    return queryString ? `${baseRoute}?${queryString}` : baseRoute;
}

/**
 * @todo - move this into it's own component file?
 * @see - https://github.com/EBSCOIS/novelist.production.libraryaware/issues/389
 */
export function PageNotFound() {
    return <div>{Constants.HEADER_DESCRIPTION_PAGE_NOT_FOUND}</div>
};


/**
 * Due to the nature of implementing react-router alongside Backbone's hash router, we are unable to use the 
 * built in <Switch> component of react-router 4. This function will switch on the currentHash of a given page and then return the expected component to render.
 *
 * @export
 * @param {string} currentHash The current hash, easily accessible by react-routers 'location.hash' prop
 * @returns {object} context for matched hash. Contains Component, page name and page description
 */
export function getComponentForMatchedRoute(currentHash) {
    switch (currentHash) {
        case ROUTE_URIS.FOLDERS_ALL_FOLDERS:
        case `${ROUTE_URIS.FOLDERS_ALL_FOLDERS}/`:
            return {
                component: AllFoldersContainer,
                description: Constants.HEADER_DESCRIPTION_ALL_FOLDERS,
                name: Constants.HEADER_TEXT_ALL_FOLDERS
            };
        case ROUTE_URIS.FOLDERS_MY_FOLDERS:
        case `${ROUTE_URIS.FOLDERS_MY_FOLDERS}/`:
            return {
                component: MyFoldersContainer,
                description: Constants.HEADER_DESCRIPTION_MY_FOLDERS,
                name: Constants.HEADER_TEXT_MY_FOLDERS
            };
        case ROUTE_URIS.FOLDERS_RECENTLY_UPDATED_FOLDERS:
        case `${ROUTE_URIS.FOLDERS_RECENTLY_UPDATED_FOLDERS}/`:
            return {
                component: RecentlyUpdatedFoldersContainer,
                description: Constants.HEADER_DESCRIPTION_RECENTLY_UPDATED_FOLDERS,
                name: Constants.HEADER_TEXT_RECENTLY_UPDATED_FOLDERS,
            }
        case ROUTE_URIS.FOLDERS_STARRED_FOLDERS:
        case `${ROUTE_URIS.FOLDERS_STARRED_FOLDERS}/`:
            return {
                component: StarredFoldersContainer,
                description: Constants.HEADER_DESCRIPTION_STARRED_FOLDERS,
                name: Constants.HEADER_TEXT_STARRED_FOLDERS
            };
        case ROUTE_URIS.FOLDERS_ARCHIVED_FOLDERS:
        case `${ROUTE_URIS.FOLDERS_ARCHIVED_FOLDERS}/`:
            return {
                component: ArchivedFoldersContainer,
                description: Constants.HEADER_DESCRIPTION_ARCHIVED_FOLDERS,
                name: Constants.HEADER_TEXT_ARCHIVED_FOLDERS
            };
        default:
            return {
                component: PageNotFound,
                /**
                 * @note - HEADER_DESCRIPTION_PAGE_NOT_FOUND is used on said page's body, not its subheader
                 * @see - PageNotFound component in this file
                 */
                description: Constants.HEADER_TEXT_PAGE_NOT_FOUND,
                name: Constants.HEADER_TEXT_PAGE_NOT_FOUND,
            };
    }
}

// Represents a collection of our known React routes.
const reactAppRoutes = ["/folders"];


/**
 * Determines if whether or not the current URL is a designated route for our new React app. The react-router
 * provides access to the URL hash which we can use to determine if we should continue with rendering.
 *
 * @param {string} hash The current URL hash provided by react-router
 * @returns {boolean} Returns True if the hash is one of our allowed routes, otherwise returns False.
 */
export function shouldRenderRoute(hash) {
    let shouldRender = false;
		
    reactAppRoutes.forEach(route => {
        if (hash && hash.includes(route)) {
            shouldRender = true;
        }
    })

    return shouldRender;
}